import { useRef, useCallback } from 'react';
import { RATE_LIMIT_MAX, RATE_LIMIT_WINDOW_MS } from '../constants';

/**
 * Sliding-window rate limiter. Tracks request timestamps in a ref
 * to avoid unnecessary re-renders.
 *
 * @param {number} [limit=RATE_LIMIT_MAX]
 * @param {number} [windowMs=RATE_LIMIT_WINDOW_MS]
 * @returns {{
 *   canRequest: () => boolean,
 *   recordRequest: () => void,
 *   msUntilReset: () => number
 * }}
 */
export function useRateLimiter(limit = RATE_LIMIT_MAX, windowMs = RATE_LIMIT_WINDOW_MS) {
  const timestamps = useRef([]);

  const pruneStale = useCallback(() => {
    const cutoff = Date.now() - windowMs;
    timestamps.current = timestamps.current.filter((t) => t > cutoff);
  }, [windowMs]);

  const canRequest = useCallback(() => {
    pruneStale();
    return timestamps.current.length < limit;
  }, [pruneStale, limit]);

  const recordRequest = useCallback(() => {
    timestamps.current.push(Date.now());
  }, []);

  const msUntilReset = useCallback(() => {
    pruneStale();
    if (timestamps.current.length === 0) return 0;
    const oldest = Math.min(...timestamps.current);
    return Math.max(0, oldest + windowMs - Date.now());
  }, [pruneStale, windowMs]);

  return { canRequest, recordRequest, msUntilReset };
}
